import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../language/LanguageProvider.jsx';
import { useAuth } from '../other/auth';
import { getLogo } from '../utils/logo';
import { media } from '../other/breakpoints';
import NavLink from './NavBar/NavLink';
import NavMenuButton from './NavBar/NavMenuButton';
import ActionButton from './NavBar/ActionButton';
import ProfileDropdown from './NavBar/ProfileDropdown';
import Dropdown from './Dropdown/Dropdown';
import MenuItem from './Dropdown/MenuItem';
import './NavBar/NavBar.css';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4rem;
  display: flex;
  align-items: center;
  padding: 0 2rem;
  z-index: 1000;
  background-color: transparent;
  transition: background-color 0.3s ease;

  &.scrolled {
    background-color: var(--first-color);
  }

  &.fullscreen-hidden {
    display: none;
  }

  ${media.mobileMenu} {
    height: 4.5rem;
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${media.mobileMenu} {
    display: none;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;

  img {
    height: 2rem;
    max-height: 2rem;
  }
`;

const ActionsMenuButton = styled.button.attrs({
  type: 'button'
})`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #829196;
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  outline: none;

  &:hover {
    background: var(--second-color);
  }

  &:focus {
    outline: none;
  }
`;

const ActionsMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
  min-width: 12rem;

  a {
    margin-right: 0;
    width: 100%;
    justify-content: flex-start;
    border-radius: 0;
    height: auto;
    padding: 0.75rem 1.5rem;
    
    &:hover {
      width: 100%;
      border-radius: 0;
    }
  }
`;

const NavBar = () => {
  const fullScreen = useSelector((state) => state.fullScreen);
  const location = useLocation();
  const auth = useAuth();
  const { t } = useLang();
  const { playingMode } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const [isNavigateMenuOpen, setIsNavigateMenuOpen] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 900);
      if (window.innerWidth >= 900) {
        setIsNavigateMenuOpen(false);
        setIsActionsMenuOpen(false);
      }
    };
    
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  useEffect(() => {
    setIsNavigateMenuOpen(false);
    setIsActionsMenuOpen(false);
  }, [location.pathname]);

  const isAuthenticated = auth.isAuth();
  const showActions = isAuthenticated && !location.pathname.includes('menu');
  const isLiveSection = location.pathname.includes('live');
  const isMovieSection = location.pathname.includes('movie');
  const isSeriesSection = location.pathname.includes('series');
  const showSearchButton = (isLiveSection || isMovieSection || isSeriesSection) && showActions;
  const showCategoryButton = showActions && (isLiveSection || isMovieSection || isSeriesSection);

  return (
    <Nav 
      className={`navbar ${scrolled ? 'scrolled' : ''} ${fullScreen ? 'fullscreen-hidden' : ''}`}
    >
      <NavContainer>
        <NavLeft>
          <LogoLink to="/">
            <img src={getLogo()} alt="Logo" />
          </LogoLink>
          
          {isAuthenticated && isMobile && (
            <Dropdown
              isOpen={isNavigateMenuOpen}
              onClose={() => setIsNavigateMenuOpen(false)}
              trigger={
                <NavMenuButton
                  isOpen={isNavigateMenuOpen}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsNavigateMenuOpen(prev => !prev);
                  }}
                >
                  {t('nav.browse')}
                </NavMenuButton>
              }
              position="bottom"
              netflixStyle={true}
            >
              <MenuItem to="/" exact>
                {t('nav.home') || 'Início'}
              </MenuItem>
              <MenuItem to="/live/category/">
                {t('lateral.live')}
              </MenuItem>
              <MenuItem to="/movie/">
                {t('lateral.movies')}
              </MenuItem>
              <MenuItem to="/series/">
                {t('lateral.series')}
              </MenuItem>
            </Dropdown>
          )}
        </NavLeft>

        {isAuthenticated && (
          <NavCenter>
            <NavLink to="/" exact>
              {t('nav.home') || 'Início'}
            </NavLink>
            <NavLink to="/live/category/">
              {t('lateral.live')}
            </NavLink>
            <NavLink to="/movie/">
              {t('lateral.movies')}
            </NavLink>
            <NavLink to="/series/">
              {t('lateral.series')}
            </NavLink>
          </NavCenter>
        )}

        <NavRight>
          {showActions && (
            <>
              {isMobile ? (
                <Dropdown
                  isOpen={isActionsMenuOpen}
                  onClose={() => setIsActionsMenuOpen(false)}
                  trigger={
                    <ActionsMenuButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsActionsMenuOpen(!isActionsMenuOpen);
                      }}
                      aria-label="Menu"
                    >
                      <i className="fas fa-ellipsis-v"></i>
                    </ActionsMenuButton>
                  }
                  position="bottom"
                  netflixStyle={true}
                >
                  <ActionsMenuContent>
                    {isLiveSection && (
                      <ActionButton
                        to={`${location.pathname.split('?')[0]}tvguide/`}
                        icon="fas fa-tv"
                        label={t('nav.tvGuide')}
                        inMenu={true}
                      />
                    )}

                    {showSearchButton && (
                      <ActionButton
                        to={{
                          pathname: `${location.pathname.split('?')[0]}search/`,
                          search: window.location.search,
                        }}
                        icon="fas fa-search"
                        label={t('nav.search')}
                        inMenu={true}
                      />
                    )}

                    {showCategoryButton && (
                      <ActionButton
                        to={{
                          pathname: `/${playingMode}/category/`,
                          search: window.location.search,
                        }}
                        icon="fas fa-grip-lines"
                        label={t('nav.selectCategory')}
                        inMenu={true}
                      />
                    )}

                    {isAuthenticated && <ProfileDropdown inMenu={true} />}
                  </ActionsMenuContent>
                </Dropdown>
              ) : (
                <>
                  {isLiveSection && (
                    <ActionButton
                      to={`${location.pathname.split('?')[0]}tvguide/`}
                      icon="fas fa-tv"
                      label={t('nav.tvGuide')}
                      expandWidth="8rem"
                    />
                  )}

                  {showSearchButton && (
                    <ActionButton
                      to={{
                        pathname: `${location.pathname.split('?')[0]}search/`,
                        search: window.location.search,
                      }}
                      icon="fas fa-search"
                      label={t('nav.search')}
                      expandWidth="8rem"
                    />
                  )}

                  {showCategoryButton && (
                    <ActionButton
                      to={{
                        pathname: `/${playingMode}/category/`,
                        search: window.location.search,
                      }}
                      icon="fas fa-grip-lines"
                      label={t('nav.selectCategory')}
                      expandWidth="14rem"
                    />
                  )}

                  {isAuthenticated && <ProfileDropdown />}
                </>
              )}
            </>
          )}
        </NavRight>
      </NavContainer>
    </Nav>
  );
};

export default NavBar;
